import { useState } from "react";
import {
  Datagrid,
  FunctionField,
  Record as RaRecord,
  Button,
  useTranslate,
  useListContext,
  Filter,
  TextInput,
  SelectInput,
} from "react-admin";
import { format } from "date-fns";
import {
  Box,
  Typography,
  Chip,
  makeStyles,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
} from "@material-ui/core";
import { IResourceListProps } from "../../common/ra-types";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import DescriptionIcon from "@material-ui/icons/Description";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ELDFileExportForm from "../data-import-export/EldExportImport/EldExportForm";
import { Clear, CloudDownload } from "@material-ui/icons";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: "100%",
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  exportButton: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  datagrid: {
    width: "100% !important",
    "& .MuiTable-root": {
      width: "100% !important",
    },
    "& .MuiTableContainer-root": {
      width: "100% !important",
    },
  },
}));

const StatusChip = ({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
        return "primary";
      case "failed":
      case "created":
      case "exporting":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
        return <CheckCircleIcon fontSize="small" />;
      case "failed":
        return <ErrorIcon fontSize="small" />;
      case "created":
      case "exporting":
        return <HourglassEmptyIcon fontSize="small" />;
      default:
        return <HourglassEmptyIcon fontSize="small" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
        return "Succeeded";
      case "failed":
        return "Failed";
      case "created":
        return "Created";
      case "exporting":
        return "Exporting";
      default:
        return status;
    }
  };

  return (
    <Chip
      icon={getStatusIcon(status)}
      label={getStatusLabel(status)}
      color={getStatusColor(status)}
      size="small"
      variant="outlined"
    />
  );
};

const ListFilter = (props: any) => {
  useSearchFields(props, ["fileName"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  return (
    <Filter {...props}>
      {loading && (
        <TextInput
          className="drivers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoComplete="off"
        />
      )}
      {!loading && (
        <TextInput
          className="drivers-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      <SelectInput
        className="filter-box"
        label="resources.admin-eld-export.labels.filterByExportStatus"
        allowEmpty
        source="status"
        alwaysOn
        choices={[
          { id: "Succeeded", name: "Succeeded" },
          { id: "Failed", name: "Failed" },
          { id: "Created", name: "Created" },
          { id: "Exporting", name: "Exporting" },
        ]}
      />
    </Filter>
  );
};

export const EldExportList = (props: IResourceListProps) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const translate = useTranslate();

  const parseExportDescription = (description: string) => {
    try {
      const parsed = JSON.parse(description);
      const { dateFrom, dateTo, drivers } = parsed;
      const driverCount = drivers ? drivers.length : 0;

      return translate("resources.admin-eld-export.messages.exportDescription", {
        dateFrom: format(new Date(dateFrom), "MM/dd/yyyy"),
        dateTo: format(new Date(dateTo), "MM/dd/yyyy"),
        driverCount: driverCount,
      });
    } catch (error) {
      return description;
    }
  };

  return (
    <div className={classes.container}>
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={DescriptionIcon} />}
        className="content-list"
        title="resources.admin-eld-export.name"
        bulkActionButtons={false}
        sort={{ field: "creationTime", order: "DESC" }}
        style={{ width: "100%" }}
        filters={<ListFilter />}
        canExport={false}
        actions={
          <Button
            className={classes.exportButton}
            label="Export ELD File(s)"
            onClick={() => setIsOpen(true)}
            icon={<CloudDownload />}
          />
        }
        {...props}
      >
        <Datagrid className={classes.datagrid}>
          <FunctionField
            label="resources.admin-eld-export.fields.fileName"
            render={(record?: RaRecord) => (
              <Typography
                variant="body2"
                style={{
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={record?.fileName}
              >
                {record?.fileName}
              </Typography>
            )}
          />
          <FunctionField
            label="resources.admin-eld-export.fields.creationTime"
            render={(record?: RaRecord) => (
              <Typography variant="body2">
                {record?.creationTime
                  ? format(
                      new Date(record.creationTime),
                      "MM/dd/yyyy, hh:mm:ss a"
                    )
                  : "N/A"}
              </Typography>
            )}
          />
          <FunctionField
            label="resources.admin-eld-export.fields.creator"
            render={(record?: RaRecord) => (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span>
                  {record?.creatorName} {record?.creatorSurname}
                </span>
                <a
                  href={`mailto:${record?.creatorEmail}`}
                  style={{
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    color: "inherit",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.textDecoration = "underline")
                  }
                >
                  {record?.creatorEmail}
                </a>
              </div>
            )}
          />
          <FunctionField
            label="resources.admin-eld-export.fields.description"
            render={(record?: RaRecord) => (
              <Typography variant="body2">
                {record?.description
                  ? parseExportDescription(record.description)
                  : "N/A"}
              </Typography>
            )}
          />
          <FunctionField
            label="resources.admin-eld-export.fields.status"
            render={(record?: RaRecord) => (
              <StatusChip status={record?.status || "unknown"} />
            )}
          />
          <FunctionField
            label="resources.admin-eld-export.fields.downloadUrl"
            render={(record?: RaRecord) => (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {record?.downloadUrl ? (
                  <GetAppIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      window.open(record.downloadUrl, "_self");
                    }}
                  />
                ) : (
                  "-"
                )}
              </Box>
            )}
          />
        </Datagrid>
      </DefaultProLogsList>

      <Dialog
        fullWidth
        maxWidth="md"
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <DialogTitle>
          <div className={classes.dialogTitle}>
            <span>ELD File Export</span>
            <IconButton onClick={() => setIsOpen(false)} size="small">
              <Clear />
            </IconButton>
          </div>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.dialogContent}>
          <ELDFileExportForm onSuccess={() => setIsOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EldExportList;
